import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import styles from './UserProfile.module.css';

interface UserProfileProps {
  isMinimized?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ isMinimized = false }) => {
  const { user, logout, isLoading } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  if (!user) return null;

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const initials = user.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user.upn.charAt(0).toUpperCase();

  return (
    <div ref={dropdownRef} className={styles.userProfileContainer}>
      <div 
        className={`${styles.userAvatar} ${isDropdownOpen ? styles.active : ''} ${isMinimized ? styles.minimized : ''}`} 
        onClick={toggleDropdown}
        title={user.displayName || user.upn}
      >
        {user.photo ? (
          <img src={user.photo} alt={user.displayName} className={styles.avatarImage} />
        ) : (
          <div className={styles.avatarInitials}>{initials}</div>
        )}
        <div className={styles.statusIndicator}></div>
      </div>

      {isDropdownOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <div className={styles.dropdownAvatar}>
              {user.photo ? (
                <img src={user.photo} alt={user.displayName} className={styles.avatarImage} />
              ) : (
                <div className={styles.avatarInitials}>{initials}</div>
              )}
            </div>
            <div className={styles.dropdownUserInfo}>
              <div className={styles.dropdownUserName}>{user.displayName || 'User'}</div>
              <div className={styles.dropdownUserEmail}>{user.upn}</div>
              {user.jobTitle && <div className={styles.dropdownUserTitle}>{user.jobTitle}</div>}
              {user.department && <div className={styles.dropdownUserDepartment}>{user.department}</div>}
            </div>
          </div>
          
          <div className={styles.dropdownDivider}></div>
          
          <div className={styles.dropdownActions}>
            <button 
              onClick={handleLogout}
              disabled={isLoading}
              className={styles.logoutButton}
            >
              {isLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  Signing out...
                </>
              ) : (
                <>
                  <svg className={styles.logoutIcon} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 17v-3H9v-4h7V7l5 5-5 5M14 2a2 2 0 012 2v2h-2V4H4v16h10v-2h2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2h10z"/>
                  </svg>
                  Sign out
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
